<template>
  <!-- Main Content -->
  <main class="py-4 sm:py-8 sm:p-8">
    <!-- Header -->
    <div class="mb-10 flex items-center justify-between">
      <div>
        <PageTitle>
          Gerencie seus fluxos de chat
        </PageTitle>
        <PageDescription>
          Crie, personalize e administre seus fluxos de conversação com facilidade.
        </PageDescription>
      </div>
      <RegisterButton @click="openNewFlowDialog">Criar fluxo</RegisterButton>
    </div>

    <!-- Search and Filter -->
    <div class="mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
      <!-- <div class="relative w-full sm:w-72">
        <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input 
          v-model="searchQuery" 
          placeholder="Buscar fluxos..." 
          class="pl-10"
          @input="filterFlows"
        />
      </div> -->
      <!-- <div class="flex gap-3 w-full sm:w-auto">
        <Select v-model="statusFilter">
          <SelectTrigger class="w-full sm:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem v-for="status in FlowStatusEnum" :key="status" :value="status">
              {{ FlowStatusLabels[status] }}
            </SelectItem>
          </SelectContent>
        </Select>
        <Select v-model="sortBy">
          <SelectTrigger class="w-full sm:w-40">
            <SelectValue placeholder="Ordenar por" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Mais recentes</SelectItem>
            <SelectItem value="oldest">Mais antigos</SelectItem>
            <SelectItem value="name">Nome (A-Z)</SelectItem>
          </SelectContent>
        </Select>
      </div> -->
    </div>

    <!-- Flow Cards Grid -->
    <div v-if="flows && flows.data.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <FlowCard 
        v-for="flow in flows.data" 
        :key="flow.id" 
        :flow="flow" 
        @edit-flow="editFlow" 
        @delete-flow="deleteFlow" 
        @switch-flow="handleStatusSwitch" 
      />
    </div>
    
    <!-- Empty State -->
    <div v-else-if="flows && flows.data.length === 0" class="bg-white/5 rounded-lg p-10 text-center">
      <div class="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-white/5 to-white/10 flex items-center justify-center mb-4">
        <GitBranch class="h-8 w-8 text-teal-400" />
      </div>
      <h3 class="text-xl font-semibold text-white mb-2">Nenhum fluxo encontrado</h3>
      <p class="text-gray-400 mb-6 max-w-md mx-auto">Comece criando seu primeiro fluxo de chat para interagir com seus usuários.</p>
      <Button @click="openNewFlowDialog">
        <PlusIcon class="h-4 w-4 mr-2" />
        Criar meu primeiro fluxo
      </Button>
    </div>
    
    <!-- Loading State -->
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <div v-for="i in 8" :key="i" class="bg-gray-800 skeleton-shine rounded-lg overflow-hidden">
        <div class="h-60 bg-zinc-700/50"></div>
        <div class="p-4">
          <div class="h-6 bg-zinc-700/50 rounded w-3/4 mb-3"></div>
          <div class="h-4 bg-zinc-700/50 rounded w-full mb-2"></div>
          <div class="h-4 bg-zinc-700/50 rounded w-2/3"></div>
          <div class="flex justify-between mt-4">
            <div class="h-3 bg-zinc-700/50 rounded w-1/4"></div>
            <div class="h-3 bg-zinc-700/50 rounded w-1/4"></div>
          </div>
        </div>
        <div class="p-4 pt-0 flex justify-between">
          <div class="h-4 bg-zinc-700/50 rounded w-1/4"></div>
          <div class="h-4 bg-zinc-700/50 rounded w-1/4"></div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="mt-6">
      <Pagination 
        v-if="flows?.pagination" 
        :data="flows.pagination" 
        v-model:per-page="perPage" 
        @update:page="(page: string) => reloadAndGetAll({ page, perPage })" 
      />
    </div>
  </main>

  <!-- New/Edit Flow Dialog -->
  <Dialog v-model:open="isFlowDialogOpen" @close="closeFlowDialog">
    <DialogContent class="sm:max-w-xl">
      <DialogHeader>
        <DialogTitle>{{ editingFlow ? 'Editar fluxo' : 'Novo fluxo de chat' }}</DialogTitle>
        <DialogDescription>
          Configure as informações do seu fluxo de conversação.
        </DialogDescription>
      </DialogHeader>
      <div class="grid gap-6 py-4">
        <div class="grid gap-2">
          <Label>Nome do fluxo <span class="text-red-500">*</span></Label>
          <Input v-model="flowForm.name" placeholder="Ex: Atendimento ao cliente" />
        </div>
        <div class="grid gap-2">
          <Label>Descrição</Label>
          <Textarea 
            v-model="flowForm.description" 
            placeholder="Descreva o propósito deste fluxo de chat..." 
            class="resize-none"
            rows="3"
          />
        </div>
        <div class="grid gap-2">
          <div class="flex items-center gap-2">
            <Label>Status <span class="text-red-500">*</span></Label>
            <TooltipProvider>
              <Tooltip :delay-duration="100">
                <TooltipTrigger as-child>
                  <CircleHelp class="size-5 text-primary" />
                </TooltipTrigger>
                <TooltipContent side="top" :side-offset="5">
                  Status inativo: Este fluxo não será <br> disponibilizado aos usuários.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Select v-model="flowForm.status">
            <SelectTrigger>
              <SelectValue placeholder="Selecione o status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="status in FlowStatusEnum" :value="status">{{ FlowStatusLabels[status] }}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="grid gap-2">
          <Label>Palavra-chave <span class="text-xs text-zinc-400">(opcional)</span></Label>
          <Input v-model="flowForm.leadStartParameterContent" placeholder="Ex: campaign-1" />
        </div>
        <div class="flex items-center gap-2">
          <Switch v-model:checked="flowForm.allowChatResume" />
          <Label>Permitir retomar conversa</Label>
          <TooltipProvider>
            <Tooltip :delay-duration="100">
              <TooltipTrigger as-child>
                <CircleHelp class="size-5 text-primary" />
              </TooltipTrigger>
              <TooltipContent side="top" :side-offset="5">
                Quando ativado, os usuários podem continuar de onde pararam ao iniciarem novamente.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <DialogFooter>
        <Button class="text-white" variant="ghost" @click="closeFlowDialog">Cancelar</Button>
        <Button :disabled="isSaving || !flowForm.name" @click="saveFlow">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin mr-2" />
          {{ editingFlow ? 'Salvar alterações' : 'Criar fluxo' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Delete Confirmation Dialog -->
  <DeleteConfirmationDialog 
    v-model:open="isDeleteFlowDialogOpen" 
    :loading="isDeletingFlow" 
    title="Excluir fluxo de chat"
    description="Tem certeza que deseja excluir este fluxo? Esta ação não pode ser desfeita e todos os dados associados serão perdidos."
    @confirm="confirmDeleteFlow" 
  />

  <Dialog v-model:open="isSwitchConfirmationDialogOpen">
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Ativar este fluxo?</DialogTitle>
        <DialogDescription>
          Ao ativar este fluxo, todos os outros fluxos ativos serão automaticamente desativados. 
          Você tem certeza que deseja continuar?
        </DialogDescription>
      </DialogHeader>
      <DialogFooter class="gap-2">
        <Button variant="outline" class="text-white" @click="isSwitchConfirmationDialogOpen = false">
          Cancelar
        </Button>
        <Button @click="confirmSwitchFlow">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin mr-2" />
          Confirmar
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  PlusIcon,
} from '@heroicons/vue/24/outline'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { CircleHelp, GitBranch } from 'lucide-vue-next'
import RegisterButton from '@/components/ui/register-button/RegisterButton.vue'
import { useToast } from '@/components/ui/toast'
import { Loader2 } from 'lucide-vue-next'
import DeleteConfirmationDialog from '@/components/ui/delete-dialog/DeleteConfirmationDialog.vue'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { CreateFlowDTO, flows, GetAllFlowsParams, useFlow } from '@/composables/chatFlow/useFlow'
import { Pagination } from '@/interfaces/Pagination'
import { Flow } from '@/interfaces/chat-flow/flow/Flow'
import { FlowStatusEnum, FlowStatusLabels } from '@/enums/chatFlow/flows/FlowStatusEnum'
import { useLeadStartParameters } from '@/composables/useLeadStartParameters'
import { LeadStartParameter } from '@/interfaces/leads/LeadStartParam'
import FlowCard from './components/FlowCard.vue'

// State
const { getAll, create, update, destroy } = useFlow()
const { getAll: getAllLeadStartParameters } = useLeadStartParameters()
const leadStartParameters = ref<Pagination<LeadStartParameter>>()
const perPage = ref('12')
const isFlowDialogOpen = ref(false)
const isDeleteFlowDialogOpen = ref(false)
const isSaving = ref(false)
const isDeletingFlow = ref(false)
const editingFlow = ref<number | null>(null)
const deletingFlow = ref<number | null>(null)
const flowForm = ref<Partial<CreateFlowDTO>>({})
const { toast } = useToast()
const isSwitchConfirmationDialogOpen = ref(false)
const flowToSwitch = ref<Flow | null>(null)

const openNewFlowDialog = () => {
  editingFlow.value = null
  flowForm.value = { 
    status: FlowStatusEnum.ACTIVE,
  }
  isFlowDialogOpen.value = true
}

const closeFlowDialog = () => {
  isFlowDialogOpen.value = false
  flowForm.value = {}
}

const editFlow = (flow: Flow) => {
  editingFlow.value = flow.id
  flowForm.value = { 
    name: flow.name,
    description: flow.description,
    status: flow.status,
    allowChatResume: flow.allowChatResume, 
    leadStartParameterContent: flow.leadStartParameter ? flow.leadStartParameter?.content : null
  } as unknown as Partial<CreateFlowDTO>
  isFlowDialogOpen.value = true
}

const saveFlow = async () => {
  isSaving.value = true

  if (!editingFlow.value) {
      await create({ ...flowForm.value as CreateFlowDTO })
      
      flows.value = await getAll()
      
      toast({
        title: 'Fluxo criado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
    } else {
      console.log(editingFlow.value, { ...flowForm.value })
      await update(editingFlow.value, { ...flowForm.value })
      const index = flows.value?.data.findIndex(flow => flow.id === editingFlow.value)
      if (index !== undefined && index !== -1 && flows.value) {
        flows.value.data[index] = {
          ...flows.value.data[index],
          name: flowForm.value.name || flows.value.data[index].name,
          description: flowForm.value.description || flows.value.data[index].description,
          status: flowForm.value.status || flows.value.data[index].status,
        }
      }
      
      toast({
        title: 'Fluxo atualizado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
    }
    
    isSaving.value = false
    closeFlowDialog()
}

const deleteFlow = (flow: Flow) => {
  deletingFlow.value = flow.id
  isDeleteFlowDialogOpen.value = true
}

const confirmDeleteFlow = async () => {
  isDeletingFlow.value = true
  
  await destroy(deletingFlow.value!)
  
  if (flows.value && deletingFlow.value) {
    const index = flows.value.data.findIndex(flow => flow.id === deletingFlow.value)
    if (index !== -1) {
      flows.value.data.splice(index, 1)
    }
  }
  
  toast({
    title: 'Fluxo excluído com sucesso',
    class: 'bg-green-500 text-white',
    duration: 1500
  })
  
  isDeletingFlow.value = false
  isDeleteFlowDialogOpen.value = false
}

const reloadAndGetAll = (params: GetAllFlowsParams) => {
  // Simulate API call to get flows with pagination
  fetchFlows(params)
}

// Mock API
const fetchFlows = (params?: GetAllFlowsParams) => {
  flows.value = undefined
  
  getAll(params).then(response => {
    flows.value = response
  })
}

const handleStatusSwitch = async (flow: Flow) => {
  if (flow.status === FlowStatusEnum.ACTIVE) {
    // Se está tentando desativar, apenas atualize sem confirmação
    await updateFlowStatus(flow.id, FlowStatusEnum.INACTIVE)
  } else {
    // Se está tentando ativar, mostre o modal de confirmação
    flowToSwitch.value = flow
    isSwitchConfirmationDialogOpen.value = true
  }
}

const updateFlowStatus = async (flowId: number, status: FlowStatusEnum) => {
  try {
    await update(flowId, { status })
    
    // Atualize o flow localmente
    if (flows.value) {
      const index = flows.value.data.findIndex(f => f.id === flowId)
      if (index !== -1) {
        flows.value.data[index].status = status
      }
    }
    
    toast({
      title: 'Status atualizado com sucesso',
      class: 'bg-green-500 text-white',
      duration: 1500
    })
  } catch (error) {
    toast({
      title: 'Erro ao atualizar status',
      description: 'Ocorreu um erro ao atualizar o status do fluxo.',
      class: 'bg-red-500 text-white',
      duration: 1500
    })
  }
}

const confirmSwitchFlow = async () => {
  if (!flowToSwitch.value) return
  
  try {
    // Desative todos os outros flows ativos
    if (flows.value) {
      const activeFlows = flows.value.data.filter(f => f.status === FlowStatusEnum.ACTIVE)
      for (const flow of activeFlows) {
        await update(flow.id, { status: FlowStatusEnum.INACTIVE })
      }
    }
    
    // Ative o flow selecionado
    await updateFlowStatus(flowToSwitch.value.id, FlowStatusEnum.ACTIVE)
  } catch (error) {
    toast({
      title: 'Erro ao atualizar fluxos',
      description: 'Ocorreu um erro ao tentar ativar este fluxo e desativar os outros.',
      class: 'bg-red-500 text-white',
      duration: 1500
    })
  } finally {
    isSwitchConfirmationDialogOpen.value = false
    flowToSwitch.value = null
  }
}

// Watchers
watch([isFlowDialogOpen], () => {
  if (isFlowDialogOpen.value) {
    getAllLeadStartParameters({ perPage: '50' }).then(response => {
      leadStartParameters.value = response
    })
  }
})

watch(() => perPage.value, () => {
  reloadAndGetAll({ page: '1', perPage: perPage.value })
})
</script>